// ENVIRONMENT
import { environment } from '../../environments/environment';

// ANGULAR
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS
import { Observable } from 'rxjs';

// MODELS
import { FLOW_GATEWAY } from '@workheld/workheld-shared-lib';

/**
 * Minimum length of a BOM node search term after trimming. Shorter terms are
 * rejected by the backend with `INSTALLED_BASE_BR_BOMNODE_17`, so the client
 * does not send them at all.
 */
export const BOM_NODE_SEARCH_MIN_TERM_LENGTH = 3;

/**
 * One node of a `GET bomnode/{id}/childnodes/search` result tree (WHP-12440).
 *
 * Lean on purpose — no skills, flex properties or picture URLs, because the
 * search is keystroke-driven. Two fields carry the search semantics:
 *
 * - `searchMatch` separates a real hit from a node that is only present to show
 *   the path down to a hit. It is set on every node, on every level.
 * - `numChildNodes` always reflects the real tree, never the number of
 *   `childNodes` returned here — that is how a matched node whose children were
 *   pruned can still be expanded further.
 */
export interface IWHBomNodeSearchTreeDTO {
  id: string;
  extId: string;
  fullQualifiedExtId: string;
  name: string;
  type: string;
  parentNodeId: string;
  sortPosition: number;
  bomNodeStatus: string;
  entityStatus: 'ACTIVE' | 'INACTIVE';
  numChildNodes: number;
  searchMatch: boolean;
  childNodes: IWHBomNodeSearchTreeDTO[];
}

export interface IWHBomNodeSearchResultDTO {
  /** Matching nodes in the whole tree, counted before the server-side cap. */
  totalMatches: number;
  /** Matches actually contained in `trees` — `min(totalMatches, 200)`. */
  returnedMatches: number;
  /** `true` when the cap was hit and the user should narrow the term. */
  truncated: boolean;
  /**
   * Forest of pruned subtrees, each rooted at a direct child of the searched
   * root node. Not capped itself — its size is unrelated to the match counts.
   */
  trees: IWHBomNodeSearchTreeDTO[];
}

export const EMPTY_BOM_NODE_SEARCH_RESULT: IWHBomNodeSearchResultDTO = {
  totalMatches: 0,
  returnedMatches: 0,
  truncated: false,
  trees: [],
};

@Injectable({ providedIn: 'root' })
export class BomNodeAsyncService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Free-text search over the whole BOM node tree below a root node (WHP-12440).
   *
   * `rootBomNodeId` must be a root node — searching from a mid-tree node is
   * rejected by the backend with `INSTALLED_BASE_BR_BOMNODE_18`.
   */
  public searchChildBomNodes(
    rootBomNodeId: string,
    searchTerm: string,
    includeDecommissioned: boolean = false,
  ): Observable<IWHBomNodeSearchResultDTO> {
    const httpParams: HttpParams = new HttpParams()
      .set('searchTerm', searchTerm)
      .set('includeDecommissioned', includeDecommissioned);

    return this.httpClient.get<IWHBomNodeSearchResultDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/bomnode/${rootBomNodeId}/childnodes/search`,
      { params: httpParams },
    );
  }
}
